import {
  FRAME_CMD,
  FRAME_CMD_RESP,
  FRAME_EVENT,
  FRAME_OTA_BEGIN,
  FRAME_OTA_ABORT,
  FRAME_OTA_HEADER,
  FRAME_OTA_CHUNK,
  FRAME_OTA_END,
  FRAME_OTA_VERIFY,
  FRAME_OTA_COMMIT,
  FRAME_OTA_ACK,
  FRAME_OTA_ERR,
  FRAME_OTA_STATUS,
  OTA_FILE_COMPLETE,
  buildFrame,
  FrameReceiver,
} from './CobsProtocol.js';

const CMD_TIMEOUT_MS = 5000;
const CHUNK_SIZE = 1024;

// CobsConnection wraps a Web Serial port and communicates using the COBS
// framing protocol defined in the USB Serial Framing Protocol — COBS spec.
//
// Key differences from UsbConnection (text protocol):
//   - The port stays open for the lifetime of the device session rather than
//     being opened and closed around every command.
//   - A background read loop runs continuously, routing each complete frame to
//     the appropriate pending-command promise via the global SEQ number.
//   - sendCommand() returns a Promise that resolves with the response string
//     when the matching CMD_RESP frame arrives.

export default class CobsConnection {
  constructor(port) {
    this.port        = port;
    this.description = 'USB';

    this._txSeq           = 0;
    this._pendingCommands = new Map(); // keyed by global SEQ
    this._pendingAcks     = new Map(); // keyed by global SEQ, for OTA_ACK/OTA_ERR
    this._receiver        = new FrameReceiver();
    this._readLoopActive  = false;
    this._reader          = null;

    // Optional hooks for verbose logging in the UI.
    // onFrameSent(packet: Uint8Array) — called after each CMD packet is written.
    // onFrameReceived(frame: {type, seq, payload}) — called for every decoded frame.
    this.onFrameSent     = null;
    this.onFrameReceived = null;

    // onOtaStatus(payload: Uint8Array) — called when an OTA_STATUS frame arrives.
    this.onOtaStatus = null;

    // onPushEvent(msg: object) — called for every FRAME_EVENT (unsolicited push event).
    this.onPushEvent = null;
  }

  // Opens the port (if not already open) and starts the background read loop.
  // Safe to call multiple times — subsequent calls are no-ops.
  async open(baudRate = 115200) {
    if (this.port.readable == null) {
      // bufferSize: the Web Serial default is 255 bytes. A full DOWNLOAD
      // response (config.json, up to the 8 KB frame limit) arrives as one
      // fast burst; with the default buffer the browser's serial pipe can
      // stall mid-frame after a couple of fills (observed in Chromium: exactly
      // 510 bytes delivered, then silence — every large command times out).
      // 16 KB comfortably holds the largest possible frame in a single fill.
      await this.port.open({ baudRate, bufferSize: 16384 });
    }
    if (!this._readLoopActive) {
      this._readLoopActive = true;
      this._receiver.reset();
      this._runReadLoop(); // Fire-and-forget; errors are logged internally
    }
  }

  // Stops the read loop and closes the port.
  // Rejects any commands that are still waiting for a response.
  async close() {
    this._readLoopActive = false;

    // Cancel any in-flight commands
    for (const [seq, pending] of this._pendingCommands) {
      clearTimeout(pending.timeout);
      pending.reject(new Error('Connection closed'));
    }
    this._pendingCommands.clear();

    // Cancel any in-flight ACK waits
    for (const [seq, pending] of this._pendingAcks) {
      clearTimeout(pending.timeout);
      pending.reject(new Error('Connection closed'));
    }
    this._pendingAcks.clear();

    // Cancelling the active reader unblocks the read() await in _runReadLoop,
    // which then releases the lock so port.close() can proceed.
    if (this._reader) {
      try { await this._reader.cancel(); } catch { /* already closed */ }
      this._reader = null;
    }

    await this.port.close();
  }

  // Sends a text command and returns a Promise that resolves with the
  // response string when the matching CMD_RESP frame arrives. Rejects if the
  // device's response is one of its "ERR ..." error strings (see
  // command_interpreter.py) — every command funnels through here, so this is
  // the single place to surface those failures instead of letting callers
  // treat an error string as a successful response.
  //
  // command: the command name string, e.g. "GET_ACTIVE_PARTITION"
  // body:    optional plain JS value (object, array, etc.) to attach as a
  //          JSON body. When provided the wire payload is:
  //            "COMMAND_NAME\n{...JSON...}"
  //          The device splits on the first newline.
  async sendCommand(command, body = null, timeoutMs = CMD_TIMEOUT_MS) {
    const commandStr = body !== null
      ? `${command}\n${JSON.stringify(body)}`
      : command;
    const payload = new TextEncoder().encode(commandStr);
    const response = await this._sendFrameAndWait(FRAME_CMD, payload, timeoutMs);
    if (response.startsWith('ERR')) {
      throw new Error(response);
    }
    return response;
  }

  // Initiates an OTA firmware update session.
  // version: firmware version string, e.g. "0.5.0"
  // Resolves with the target partition letter ("A" or "B") on success.
  //
  // The device expects the payload to be a JSON object: {"version": "0.5.0"}
  async otaBegin(version) {
    // The device expects the exact format: {"version": "x.y.z"} — with a
    // space after the colon — matching the Python json.dumps() default output.
    const payload = new TextEncoder().encode(`{"version": "${version}"}`);
    // Use a longer timeout: the device clears the entire inactive partition
    // (recursive delete of all files/dirs) before responding, which can take
    // several seconds on a full firmware.
    const response = await this._sendFrameAndWait(FRAME_OTA_BEGIN, payload, 30000);
    let parsed;
    try { parsed = JSON.parse(response); } catch { /* not JSON — fall through */ }
    if (parsed?.ok === true && parsed?.target_partition) {
      return parsed.target_partition; // "A" or "B"
    }
    throw new Error(`OTA begin failed: ${response}`);
  }

  // Aborts an in-progress OTA session.
  // Resolves with the raw response text from the device.
  async otaAbort() {
    return this._sendFrameAndWait(FRAME_OTA_ABORT, new Uint8Array());
  }

  // Asks the device to verify all transferred files (checksums vs version.json).
  // Resolves with the verified version string (e.g. "0.5.0") on success.
  // Rejects if verification fails or times out.
  async otaVerify() {
    const response = await this._sendFrameAndWait(
      FRAME_OTA_VERIFY, new Uint8Array(), 10000
    );
    if (response.startsWith('FIRMWARE_VERIFIED')) {
      return response.split(' ')[1]; // e.g. "0.5.0"
    }
    throw new Error(`OTA verify failed: ${response}`);
  }

  // Commits the firmware update — device copies config, writes boot flag, reboots.
  // Resolves when the device sends "FIRMWARE_COMMITTED" before it reboots.
  async otaCommit() {
    return this._sendFrameAndWait(FRAME_OTA_COMMIT, new Uint8Array());
  }

  // Runs a complete firmware update from begin → file transfer → verify → commit.
  //
  // manifest: { version: string, files: Array<{ path: string, data: Uint8Array, sha256?: string }> }
  //   - version.json must be present and will always be sent last.
  // onProgress: optional callback({ stage, ...details }) for UI status updates.
  //
  // Calls otaAbort() and re-throws on any error to leave the device in a clean state.
  async runFirmwareUpdate(manifest, onProgress) {
    onProgress?.({ stage: 'begin' });
    const partition = await this.otaBegin(manifest.version);
    onProgress?.({ stage: 'ready', partition });

    try {
      // version.json must always be the last file transferred
      const versionFile = manifest.files.find(f => f.path === 'version.json');
      const otherFiles  = manifest.files.filter(f => f.path !== 'version.json');
      const orderedFiles = versionFile ? [...otherFiles, versionFile] : otherFiles;

      for (let i = 0; i < orderedFiles.length; i++) {
        const file = orderedFiles[i];
        onProgress?.({ stage: 'transferring', file: file.path, fileIndex: i, totalFiles: orderedFiles.length });
        await this.otaSendFile(file.path, file.data, file.sha256 ?? null);
      }

      onProgress?.({ stage: 'verifying' });
      const verifiedVersion = await this.otaVerify();
      onProgress?.({ stage: 'verified', version: verifiedVersion });

      onProgress?.({ stage: 'committing' });
      await this.otaCommit();
      onProgress?.({ stage: 'committed' });

    } catch (err) {
      try { await this.otaAbort(); } catch { /* ignore — device may already be in error state */ }
      throw err;
    }
  }

  // Sends a file to the device using the OTA file transfer protocol.
  //
  // filePath: path string to use in the OTA_FILE_HEADER (e.g. "/firmware.bin")
  // fileData: Uint8Array of file contents
  // sha256:   optional hex string of the SHA-256 digest (64 hex chars)
  //
  // Resolves when the device acknowledges the final OTA_FILE_END frame
  // (i.e. sends OTA_ACK with ota_seq_next = 0xFF).
  async otaSendFile(filePath, fileData, sha256 = null) {
    // Build OTA_FILE_HEADER payload:
    //   [P_HI, P_LO] [path bytes] [S_HI, S_MID_HI, S_MID_LO, S_LO] [sha256_flag] [sha256 (32 bytes, if flag=0x01)]
    const pathBytes = new TextEncoder().encode(filePath);
    const pHi = (pathBytes.length >> 8) & 0xFF;
    const pLo = pathBytes.length & 0xFF;

    const fileSize = fileData.length;
    const sHi    = (fileSize >> 24) & 0xFF;
    const sMidHi = (fileSize >> 16) & 0xFF;
    const sMidLo = (fileSize >> 8)  & 0xFF;
    const sLo    = fileSize         & 0xFF;

    const sha256Flag  = sha256 ? 0x01 : 0x00;
    const sha256Bytes = sha256 ? this._hexToBytes(sha256) : new Uint8Array(0);

    const headerPayload = new Uint8Array(
      2 + pathBytes.length + 4 + 1 + sha256Bytes.length
    );
    let off = 0;
    headerPayload[off++] = pHi;
    headerPayload[off++] = pLo;
    headerPayload.set(pathBytes, off); off += pathBytes.length;
    headerPayload[off++] = sHi;
    headerPayload[off++] = sMidHi;
    headerPayload[off++] = sMidLo;
    headerPayload[off++] = sLo;
    headerPayload[off++] = sha256Flag;
    if (sha256Bytes.length > 0) {
      headerPayload.set(sha256Bytes, off);
    }

    // OTA_FILE_HEADER has no response — send and proceed immediately.
    await this._sendFrameOnly(FRAME_OTA_HEADER, headerPayload);

    // Send ALL file data as OTA_FILE_CHUNK frames, stop-and-wait for each ACK.
    // The device writes data on CHUNK frames only; END is a separate empty signal.
    let offset = 0;
    let otaSeq = 0;

    while (offset < fileData.length) {
      const chunkData = fileData.slice(offset, offset + CHUNK_SIZE);

      // OTA_FILE_CHUNK payload: [ota_seq (1 byte)] [data]
      const chunkPayload = new Uint8Array(1 + chunkData.length);
      chunkPayload[0] = otaSeq;
      chunkPayload.set(chunkData, 1);

      const ackPayload = await this._sendOtaFrameAndWaitAck(FRAME_OTA_CHUNK, chunkPayload);

      // Device echoes the next expected ota_seq in the ACK payload
      otaSeq = ackPayload.length > 0 ? ackPayload[0] : (otaSeq + 1) & 0xFF;
      offset += chunkData.length;
    }

    // OTA_FILE_END signals end-of-transfer; payload is empty.
    const finalAck = await this._sendOtaFrameAndWaitAck(FRAME_OTA_END, new Uint8Array());

    if (finalAck.length < 1 || finalAck[0] !== OTA_FILE_COMPLETE) {
      const got = finalAck[0]?.toString(16) ?? 'none';
      throw new Error(`OTA file end: unexpected ota_seq_next=0x${got} (expected 0xFF)`);
    }
  }

  // --- Internal ---

  // Builds and sends a single COBS frame, returns a Promise that resolves
  // with the decoded response payload text when the matching CMD_RESP arrives.
  // All public send methods delegate to this.
  async _sendFrameAndWait(frameType, payload, timeoutMs = CMD_TIMEOUT_MS) {
    const seq = this._nextSeq();

    // Register the pending promise BEFORE writing to the port so we never
    // miss a response that arrives before the await returns.
    const responsePromise = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this._pendingCommands.delete(seq);
        reject(new Error(`COBS frame timed out (type=0x${frameType.toString(16)})`));
      }, timeoutMs);
      this._pendingCommands.set(seq, { resolve, reject, timeout });
    });

    const packet = buildFrame(frameType, seq, payload);
    const writer = this.port.writable.getWriter();
    try {
      await writer.write(packet);
      if (this.onFrameSent) this.onFrameSent(packet);
    } finally {
      writer.releaseLock();
    }

    return responsePromise;
  }

  // Sends an OTA frame (CHUNK or END) and waits for the matching OTA_ACK.
  // Registers the pending-ack entry BEFORE writing so no ACK can arrive
  // between the write and the registration (prevents a race condition).
  // Resolves with the ACK payload bytes.
  async _sendOtaFrameAndWaitAck(frameType, payload, timeoutMs = CMD_TIMEOUT_MS) {
    const seq = this._nextSeq();

    const ackPromise = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this._pendingAcks.delete(seq);
        reject(new Error(`OTA ACK timed out (seq=${seq})`));
      }, timeoutMs);
      this._pendingAcks.set(seq, { resolve, reject, timeout });
    });

    const packet = buildFrame(frameType, seq, payload);
    const writer = this.port.writable.getWriter();
    try {
      await writer.write(packet);
      if (this.onFrameSent) this.onFrameSent(packet);
    } catch (err) {
      const pending = this._pendingAcks.get(seq);
      if (pending) {
        clearTimeout(pending.timeout);
        this._pendingAcks.delete(seq);
      }
      throw err;
    } finally {
      writer.releaseLock();
    }

    return ackPromise;
  }

  // Sends a frame without registering any pending entry.
  // Returns the global SEQ used for the frame.
  // Used for OTA frames that have no response (e.g. OTA_FILE_HEADER).
  async _sendFrameOnly(frameType, payload) {
    const seq = this._nextSeq();
    const packet = buildFrame(frameType, seq, payload);
    const writer = this.port.writable.getWriter();
    try {
      await writer.write(packet);
      if (this.onFrameSent) this.onFrameSent(packet);
    } finally {
      writer.releaseLock();
    }
    return seq;
  }

  // Registers a pending ACK entry for the given global SEQ and returns a
  // Promise that resolves with the OTA_ACK payload when the device responds,
  // or rejects on OTA_ERR or timeout.
  _waitForAck(globalSeq, timeoutMs = CMD_TIMEOUT_MS) {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this._pendingAcks.delete(globalSeq);
        reject(new Error(`OTA ACK timed out (seq=${globalSeq})`));
      }, timeoutMs);
      this._pendingAcks.set(globalSeq, { resolve, reject, timeout });
    });
  }

  _nextSeq() {
    const seq = this._txSeq;
    this._txSeq = (this._txSeq + 1) % 255; // 0–254; 0xFF is reserved
    return seq;
  }

  async _runReadLoop() {
    let reader;
    try {
      reader = this.port.readable.getReader();
      this._reader = reader;

      while (this._readLoopActive) {
        const { value, done } = await reader.read();
        if (done) break;

        const now = Date.now();
        for (const byte of value) {
          const frame = this._receiver.feed(byte, now);
          if (frame) this._dispatchFrame(frame);
        }
      }
    } catch (err) {
      if (this._readLoopActive) {
        console.error('COBS read loop error:', err);
      }
    } finally {
      try { reader?.releaseLock(); } catch { /* already released */ }
      this._reader = null;
      this._readLoopActive = false;
    }
  }

  _dispatchFrame(frame) {
    if (this.onFrameReceived) this.onFrameReceived(frame);

    if (frame.type === FRAME_CMD_RESP) {
      const pending = this._pendingCommands.get(frame.seq);
      if (pending) {
        clearTimeout(pending.timeout);
        this._pendingCommands.delete(frame.seq);
        const responseText = new TextDecoder().decode(frame.payload);
        console.log('Received COBS response:', responseText);
        pending.resolve(responseText);
      } else {
        console.warn('COBS: received CMD_RESP for unknown SEQ', frame.seq);
      }
    } else if (frame.type === FRAME_OTA_ACK) {
      const pending = this._pendingAcks.get(frame.seq);
      if (pending) {
        clearTimeout(pending.timeout);
        this._pendingAcks.delete(frame.seq);
        pending.resolve(frame.payload);
      } else {
        console.warn('COBS: received OTA_ACK for unknown SEQ', frame.seq);
      }
    } else if (frame.type === FRAME_OTA_ERR) {
      const pending = this._pendingAcks.get(frame.seq);
      if (pending) {
        clearTimeout(pending.timeout);
        this._pendingAcks.delete(frame.seq);
        const errText = new TextDecoder().decode(frame.payload);
        pending.reject(new Error(`OTA error: ${errText}`));
      } else {
        console.warn('COBS: received OTA_ERR for unknown SEQ', frame.seq);
      }
    } else if (frame.type === FRAME_OTA_STATUS) {
      if (this.onOtaStatus) {
        this.onOtaStatus(frame.payload);
      }
    } else if (frame.type === FRAME_EVENT) {
      if (this.onPushEvent) {
        try {
          const msg = JSON.parse(new TextDecoder().decode(frame.payload));
          this.onPushEvent(msg);
        } catch (e) {
          console.warn('COBS: failed to parse push event payload', e);
        }
      }
    }
  }

  // Converts a hex string (e.g. "deadbeef...") to a Uint8Array.
  _hexToBytes(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    }
    return bytes;
  }

  readable() {
    return this.port.readable != null;
  }

  writable() {
    return this.port.writable != null;
  }
}
